import { useMemo, useReducer, useRef, useState } from 'react';
import { ErrorMode, TypingEngine } from '../../engine/TypingEngine.js';
import { formatDuration } from '../../engine/metrics.js';
import {
  DEFAULT_TARGET_ERROR_RATE,
  DEFAULT_TARGET_SPEED,
  PERIOD_LESSON_RANGES,
  SPEED_TABLE_FOR_140,
  classify,
  errorRateThreshold,
} from '../../engine/grading.js';
import { resolveLayout } from '../../engine/layouts.js';
import { LESSONS, lessonsByBlock } from '../../content/lessons.js';
import { MODES } from '../../content/reference-modes.js';
import { PRACTICE_TEXTS } from '../../content/texts.js';
import { store, useStoreRevision } from '../store';
import { Keyboard } from '../Keyboard';
import { navigate } from '../router';

const HERO_TEXT = 'je to moja prvá veta';

/** Koľko lekcií má daný režim — aby čísla na stránke neboli ručne písané. */
function countWithMode(mode: string) {
  return LESSONS.filter((lesson) => lesson.modes.includes(mode)).length;
}

export function Landing() {
  useStoreRevision();
  const settings = store.getSettings();
  const layout = resolveLayout(settings.layoutId, store.calibration(settings.layoutId));

  const attempts = store.attempts();
  const completed = store.completedLessonIds();
  const best = attempts.reduce((max, a) => Math.max(max, a.netCpm), 0);
  const nextLesson = LESSONS.find((lesson) => !completed.includes(lesson.id)) ?? LESSONS[0];
  const startHref = attempts.length === 0 ? `#/cvicenie/${LESSONS[0].id}` : `#/cvicenie/${nextLesson.id}`;

  return (
    <div className="landing">
      <section className="hero">
        <div>
          <span className="eyebrow">Slovenská metodika · 66 lekcií</span>
          <h1>
            Nauč sa písať <span className="accent">všetkými desiatimi</span>. Naslepo a bez chýb.
          </h1>
          <p className="hero-lead">
            Lekcie idú po radoch kláves, takže nikdy nenarazíš na klávesu, ktorú si sa ešte neučil.
            Meriame čisté údery a chybovosť — teda presne to, čo sa známkuje v škole.
          </p>
          <div className="hero-actions">
            <a className="btn btn-primary btn-lg" href={attempts.length === 0 ? '#/lekcie' : startHref}>
              {attempts.length === 0 ? 'Začať prvú lekciu' : `Pokračovať: lekcia ${nextLesson.number}`}
            </a>
            <a className="btn btn-lg btn-ghost" href="#/texty">Cvičiť na vlastnom texte</a>
          </div>
          <ul className="trust-row">
            <li><span className="tick">✓</span> Bez reklám a bez cookies</li>
            <li><span className="tick">✓</span> Funguje offline</li>
            <li><span className="tick">✓</span> Bez účtu a bez registrácie</li>
          </ul>
        </div>

        {/* Vizuál: skutočná klávesnica aplikácie v stave rozbehnutého cvičenia. */}
        <div className="hero-visual" aria-hidden="true">
          <div className="hero-visual-head">
            <strong style={{ fontSize: '0.95rem' }}>Lekcia 3 · Hmaty</strong>
            <span className="muted small">cieľ 78 úderov/min</span>
          </div>
          <div className="hero-visual-line">
            <span className="done">{HERO_TEXT.slice(0, 11)}</span>
            <span className="now">{HERO_TEXT[11]}</span>
            <span className="todo">{HERO_TEXT.slice(12)}</span>
          </div>
          <Keyboard layout={layout} nextChar={HERO_TEXT[11] ?? null} compact />
          <div className="hero-fingers">
            <span>malíčky</span>
            <span>prstenníky</span>
            <span>prostredníky</span>
            <span>ukazováky</span>
            <span>palce</span>
          </div>
        </div>
      </section>

      <section className="landing-section">
        <div className="section-head">
          <h2>Vyskúšaj si to hneď — bez registrácie</h2>
          <p>
            Klikni do rámčeka a napíš text. Počíta sa to naozaj: čistá rýchlosť je hrubé údery
            mínus desať trestných bodov za každú chybu, presne ako v školskej metodike.
          </p>
        </div>
        <MiniDemo />
      </section>

      <section className="landing-section">
        <div className="section-head">
          <h2>Ako to funguje</h2>
          <p>Nič zložité — štyri kroky, ktoré ťa dostanú od prvého písmena k známke.</p>
        </div>
        <div className="steps">
          <div className="step">
            <h3>Domovská pozícia</h3>
            <p>Prsty na A S D F – J K L ô. Začína sa na prostredníkoch a ukazovákoch, ako v školskej metodike.</p>
          </div>
          <div className="step">
            <h3>Postupné lekcie</h3>
            <p>{LESSONS.length} lekcií v {lessonsByBlock().length} obdobiach výučby. Každá používa len klávesy z predchádzajúcich lekcií.</p>
          </div>
          <div className="step">
            <h3>Štyri režimy cvičenia</h3>
            <p>
              {MODES.map((mode) => mode.label).join(' · ')} — drily na nové znaky, slová, vety
              a súvislý text.
            </p>
          </div>
          <div className="step">
            <h3>Známka ako v škole</h3>
            <p>Hodnotí sa rýchlosť aj chybovosť zvlášť a platí horšia známka.</p>
          </div>
        </div>
      </section>

      <section className="landing-section">
        <div className="section-head">
          <h2>Metrika, ktorá sa dá obhájiť pred učiteľom</h2>
          <p>
            Úder nie je stlačenie klávesy: číslice a veľké písmená sú dva údery, v skupine číslic
            sa Shift počíta raz, <code>ď</code> je tri a <code>Č</code> štyri. Rovnako sa počíta
            aj pri známkovaní.
          </p>
        </div>
        <div className="table-preview">
          <table>
            <caption className="visually-hidden">Tabuľka rýchlosti podľa obdobia výučby</caption>
            <thead>
              <tr>
                <th>Stupeň</th>
                <th className="num">1</th>
                <th className="num">2</th>
                <th className="num">3</th>
                <th className="num">4</th>
                <th className="num">5</th>
                <th className="num">6</th>
                <th className="num">7</th>
                <th className="num">8</th>
                <th className="num">9</th>
                <th className="num">10</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4].map((grade) => (
                <tr key={grade}>
                  <th>{grade}</th>
                  {(SPEED_TABLE_FOR_140[grade] ?? []).map((value, index) => (
                    <td className="num" key={index}>{value}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="muted small" style={{ marginTop: 10 }}>
          Čisté údery za minútu pre cieľ {DEFAULT_TARGET_SPEED}. Chybovosť pre cieľ{' '}
          {DEFAULT_TARGET_ERROR_RATE} % je {errorRateThreshold(1)} / {errorRateThreshold(2)} /{' '}
          {errorRateThreshold(3)} / {errorRateThreshold(4)} % pre stupne 1–4. Obdobia výučby
          zodpovedajú lekciám {PERIOD_LESSON_RANGES[0][0]}–{PERIOD_LESSON_RANGES[0][1]},{' '}
          {PERIOD_LESSON_RANGES[1][0]}–{PERIOD_LESSON_RANGES[9][1]}.
        </p>
      </section>

      <section className="landing-section">
        <div className="section-head">
          <h2>Prečo nie papier a prečo nie reklamy</h2>
          <p>Tri veci, ktoré sú v tomto nástroji inak než v bežných online písačkách.</p>
        </div>
        <div className="feature-grid">
          <article className="feature">
            <div className="feature-icon">🔒</div>
            <h3>Žiadne cookies, žiadne reklamy</h3>
            <p>
              Aplikácia neposiela údaje nikam. Žiadny analytický skript, žiadna reklama, žiadna
              tretia strana. Pokrok zostáva v tvojom prehliadači a dá sa zálohovať do súboru.
            </p>
          </article>
          <article className="feature">
            <div className="feature-icon">⌨</div>
            <h3>Kalibrácia klávesnice</h3>
            <p>
              Popisy kláves sa medzi verziami Windows líšia. Aplikácia si ich preto sama učí z toho,
              čo naozaj píšeš — netreba nič nastavovať ručne.
            </p>
          </article>
          <article className="feature">
            <div className="feature-icon">◎</div>
            <h3>Vidíš, kde robíš chyby</h3>
            <p>
              Po každom cvičení dostaneš najčastejšie chyby po jednotlivých klávesoch, priebeh
              rýchlosti a odporúčanie, čo precvičiť ďalej.
            </p>
          </article>
          <article className="feature">
            <div className="feature-icon">◱</div>
            <h3>Funguje aj offline</h3>
            <p>
              Dá sa nainštalovať ako aplikácia a po prvom načítaní funguje bez internetu — na
              školskom notebooku aj na tablete.
            </p>
          </article>
        </div>
      </section>

      <section className="landing-section">
        <div className="section-head">
          <h2>Pre školy a učiteľov</h2>
          <p>
            Známkovanie vychádza z rovnakých tabuliek, aké sa používajú vo výučbe strojopisu: desať
            období, čisté údery, chybovosť a horšia známka z oboch.
          </p>
        </div>
        <div className="badge-row">
          <span className="badge-pill">{countWithMode('slova')} / {LESSONS.length} lekcií v režime Slová</span>
          <span className="badge-pill">{countWithMode('vety')} lekcií s vetami</span>
          <span className="badge-pill">{countWithMode('text')} lekcií so súvislým textom</span>
          <span className="badge-pill is-coming">Triedy a zadania — pripravujeme</span>
          <span className="badge-pill is-coming">Prehľady pre učiteľa — pripravujeme</span>
        </div>
        <p className="muted small" style={{ marginTop: 14, maxWidth: '62ch' }}>
          Cvičné texty sú vlastné (licencia CC0). Výučba je bez reklám, takže sa dá použiť aj
          v triede, kde sa nesmú zobrazovať reklamy ani ukladať cookies.
        </p>
      </section>

      {attempts.length > 0 && (
        <section className="landing-section">
          <div className="section-head">
            <h2>Tvoj pokrok</h2>
            <p>Pokrok je uložený v tomto prehliadači. <a href="#/pokrok">Celý prehľad →</a></p>
          </div>
          <div className="stat-row">
            <div className="stat">
              <div className="stat-value">{completed.length} / {LESSONS.length}</div>
              <div className="stat-label">Dokončených lekcií</div>
            </div>
            <div className="stat">
              <div className="stat-value">{best}</div>
              <div className="stat-label">Najlepšie údery/min</div>
            </div>
            <div className="stat">
              <div className="stat-value">{attempts.length}</div>
              <div className="stat-label">Pokusov</div>
            </div>
            <div className="stat">
              <div className="stat-value">{store.weakKeys(3).length}</div>
              <div className="stat-label">Slabých kláves</div>
            </div>
          </div>
        </section>
      )}

      <footer className="landing-footer">
        <p style={{ margin: 0 }}>
          <strong>Písanie všetkými desiatimi</strong> — výučba strojopisu na slovenskej klávesnici.
          Bez reklám, bez cookies, bez účtov.
        </p>
        <ul className="footer-links">
          <li><a href="#/lekcie">Lekcie</a></li>
          <li><a href="#/texty">Cvičné texty</a></li>
          <li><a href="#/pokrok">Pokrok</a></li>
          <li><a href="#/sprievodca">Sprievodca</a></li>
          <li><a href="#/kalibracia">Kalibrácia klávesnice</a></li>
          <li><a href="#/nastavenia">Nastavenia</a></li>
        </ul>
        <p className="small" style={{ margin: 0 }}>
          Cvičné texty: vlastné dielo, licencia CC0. Kurikulum a metriky vychádzajú
          z metodiky používanej vo výučbe strojopisu; texty cvičení sú vlastné.
        </p>
      </footer>
    </div>
  );
}

/**
 * Živé demo na úvodnej stránke — používa ten istý engine ako lekcie.
 * Úmyselne sa nevyžaduje registrácia ani nastavenia: klikni a píš.
 */
function MiniDemo() {
  const text = 'je to moja prvá veta';
  const engine = useMemo(
    () => new TypingEngine({ text, errorMode: ErrorMode.FLOW, officialStrokes: true }),
    [],
  );
  const [, forceRender] = useReducer((n: number) => n + 1, 0);
  const [started, setStarted] = useState(false);
  const displayRef = useRef<HTMLDivElement>(null);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.nativeEvent.isComposing || event.key === 'Dead') {
      event.preventDefault();
      return;
    }
    if (event.key === 'Backspace') {
      event.preventDefault();
      engine.backspace(Date.now());
      forceRender();
      return;
    }
    if (event.key === 'Escape') {
      displayRef.current?.blur();
      return;
    }
    if (event.key.length === 1) {
      event.preventDefault();
      setStarted(true);
      engine.typeChar(event.key, Date.now(), event.code, {
        shift: event.shiftKey,
        capsLock: typeof event.getModifierState === 'function' ? event.getModifierState('CapsLock') : false,
      });
      forceRender();
    }
  };

  const now = Date.now();
  const metrics = engine.liveMetrics(now);
  const done = engine.finished;

  const characters = [...text].map((char, index) => {
    const status = engine.status[index];
    const isCurrent = index === engine.index && !done;
    const className = [
      'char',
      status === 'correct' ? 'is-correct' : '',
      status === 'wrong' ? 'is-wrong' : '',
      isCurrent ? 'is-current' : '',
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <span key={index} className={className}>
        {char}
      </span>
    );
  });

  return (
    <div className="demo-card">
      <div
        ref={displayRef}
        className="demo-display"
        tabIndex={0}
        role="textbox"
        aria-label="Skúšobné cvičenie — napíš text"
        aria-readonly="true"
        onKeyDown={onKeyDown}
        onPaste={(event) => event.preventDefault()}
      >
        {characters}
      </div>

      <div className="demo-metrics">
        <span>Čistá rýchlosť: <strong>{metrics.netCpm}</strong> úderov/min</span>
        <span>Chybovosť: <strong>{metrics.errorRatePercent}</strong> %</span>
        <span>Čas: <strong>{formatDuration(metrics.durationMs)}</strong></span>
        <span>Známka: <strong>{classify({ netCpm: metrics.netCpm, errorRatePercent: metrics.errorRatePercent, period: 3 }).label}</strong></span>
      </div>

      {!started && !done && (
        <p className="demo-hint">Klikni do rámčeka a začni písať — alebo stlač Tab a potom Enter.</p>
      )}
      {done && (
        <p className="demo-hint">
          Hotovo. <a href="#/lekcie">Prejdi na skutočné lekcie →</a>
        </p>
      )}

      <div className="row" style={{ marginTop: 12 }}>
        <button
          type="button"
          className="btn"
          onClick={() => {
            engine.reset();
            setStarted(false);
            forceRender();
            displayRef.current?.focus();
          }}
        >
          Skúsiť znova
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            navigate('#/lekcie');
          }}
        >
          Prejsť na lekcie
        </button>
        <span className="muted small">
          {PRACTICE_TEXTS.length} hotových textov + vlastný text
        </span>
      </div>
    </div>
  );
}
